import { useState, useRef, useCallback, useEffect } from 'react';

const SWIPE_THRESHOLD = 60;
const VIEWPORT_THRESHOLD = 0.3;
const RUBBER_BAND_FACTOR = 0.3;

// Elements that should not trigger swipe
const IGNORE_SELECTORS = '.chart-container, canvas, .trends-table, .table-container';

export default function useSwipeNavigation(viewOrder, activeView, onViewChange) {
  const [swipeOffset, setSwipeOffset] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const touchRef = useRef({ startX: 0, startY: 0, started: false, ignored: false });

  const currentIndex = viewOrder.indexOf(activeView);

  const handleTouchStart = useCallback((e) => {
    if (isAnimating) return;

    // Check if touch started inside an element we should ignore
    const target = e.target;
    if (target.closest(IGNORE_SELECTORS)) {
      touchRef.current.ignored = true;
      return;
    }

    touchRef.current = {
      startX: e.touches[0].clientX,
      startY: e.touches[0].clientY,
      started: false,
      ignored: false,
    };
  }, [isAnimating]);

  const handleTouchMove = useCallback((e) => {
    const touch = touchRef.current;
    if (touch.ignored) return;

    const dx = e.touches[0].clientX - touch.startX;
    const dy = e.touches[0].clientY - touch.startY;

    // On first significant move, check angle
    if (!touch.started) {
      const absDx = Math.abs(dx);
      const absDy = Math.abs(dy);
      if (absDx < 10 && absDy < 10) return; // Too small to determine

      // If vertical movement is greater, ignore this gesture
      if (absDy > absDx) {
        touch.ignored = true;
        return;
      }
      touch.started = true;
    }

    // Calculate offset with rubber band at edges
    let offset = dx;
    const isAtStart = currentIndex === 0 && dx > 0;
    const isAtEnd = currentIndex === viewOrder.length - 1 && dx < 0;

    if (isAtStart || isAtEnd) {
      offset = dx * RUBBER_BAND_FACTOR;
    }

    setSwipeOffset(offset);
  }, [currentIndex, viewOrder.length]);

  const handleTouchEnd = useCallback(() => {
    const touch = touchRef.current;
    if (touch.ignored || !touch.started) {
      setSwipeOffset(0);
      return;
    }

    const threshold = Math.min(SWIPE_THRESHOLD, window.innerWidth * VIEWPORT_THRESHOLD);
    setIsAnimating(true);

    if (swipeOffset > threshold && currentIndex > 0) {
      // Swipe right -> previous view
      onViewChange(viewOrder[currentIndex - 1]);
    } else if (swipeOffset < -threshold && currentIndex < viewOrder.length - 1) {
      // Swipe left -> next view
      onViewChange(viewOrder[currentIndex + 1]);
    }

    setSwipeOffset(0);
    setTimeout(() => setIsAnimating(false), 300);
  }, [swipeOffset, currentIndex, viewOrder, onViewChange]);

  useEffect(() => {
    // Only add on touch devices
    const mq = window.matchMedia('(max-width: 768px)');
    if (!mq.matches) return;

    const opts = { passive: true };
    document.addEventListener('touchstart', handleTouchStart, opts);
    document.addEventListener('touchmove', handleTouchMove, opts);
    document.addEventListener('touchend', handleTouchEnd, opts);

    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [handleTouchStart, handleTouchMove, handleTouchEnd]);

  return { swipeOffset, isAnimating };
}
